import React, { useRef, useEffect, useState } from 'react';

const COLORS = {
  provider: '#8b5cf6',
  agent: '#3b82f6',
  protocol: '#f97316',
  line: '#334155',
  pulse: '#a78bfa',
};

export default function NetworkViz({ providers, stats }) {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const [nodes, setNodes] = useState([]);
  const [pulses, setPulses] = useState([]);

  // Generate node positions
  useEffect(() => {
    const providerCount = providers?.length || 1;
    const agentCount = Math.min(stats?.total_calls_settled || 3, 12);
    const newNodes = [];

    // Protocol hub (center)
    newNodes.push({ id: 'protocol', type: 'protocol', x: 0.5, y: 0.5, label: 'THANE', size: 18 });

    // Providers (inner ring)
    for (let i = 0; i < providerCount; i++) {
      const angle = (i / Math.max(providerCount, 1)) * Math.PI * 2 - Math.PI / 2;
      const p = providers[i];
      newNodes.push({
        id: `p-${i}`, type: 'provider',
        x: 0.5 + Math.cos(angle) * 0.22,
        y: 0.5 + Math.sin(angle) * 0.22,
        label: p?.name?.slice(0, 12) || `Provider ${i + 1}`,
        size: 12,
      });
    }

    // Agents (outer ring)
    for (let i = 0; i < agentCount; i++) {
      const angle = (i / agentCount) * Math.PI * 2 + Math.PI / agentCount;
      newNodes.push({
        id: `a-${i}`, type: 'agent',
        x: 0.5 + Math.cos(angle) * 0.38,
        y: 0.5 + Math.sin(angle) * 0.38,
        label: `Agent ${i + 1}`,
        size: 7,
      });
    }

    setNodes(newNodes);
  }, [providers, stats?.total_calls_settled]);

  // Spawn random pulses
  useEffect(() => {
    const t = setInterval(() => {
      setPulses(prev => {
        const agentNodes = nodes.filter(n => n.type === 'agent');
        const providerNodes = nodes.filter(n => n.type === 'provider');
        if (agentNodes.length === 0 || providerNodes.length === 0) return prev;

        const from = agentNodes[Math.floor(Math.random() * agentNodes.length)];
        const to = providerNodes[Math.floor(Math.random() * providerNodes.length)];

        const newPulse = {
          id: Date.now(),
          fromX: from.x, fromY: from.y,
          toX: to.x, toY: to.y,
          progress: 0,
          color: Math.random() > 0.7 ? '#f97316' : '#a78bfa',
        };

        return [...prev.slice(-8), newPulse]; // keep max 8 pulses
      });
    }, 1500);
    return () => clearInterval(t);
  }, [nodes]);

  // Animate
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
    };
    resize();

    const draw = () => {
      const w = canvas.width / (window.devicePixelRatio || 1);
      const h = canvas.height / (window.devicePixelRatio || 1);
      ctx.clearRect(0, 0, w, h);

      // Draw connections
      const protocol = nodes.find(n => n.type === 'protocol');
      if (protocol) {
        nodes.forEach(n => {
          if (n.type === 'protocol') return;
          ctx.beginPath();
          ctx.moveTo(protocol.x * w, protocol.y * h);
          ctx.lineTo(n.x * w, n.y * h);
          ctx.strokeStyle = n.type === 'provider' ? 'rgba(139, 92, 246, 0.15)' : 'rgba(59, 130, 246, 0.08)';
          ctx.lineWidth = n.type === 'provider' ? 1.5 : 0.5;
          ctx.stroke();
        });
      }

      // Draw pulses
      setPulses(prev => {
        const updated = [];
        for (const p of prev) {
          p.progress += 0.015;
          if (p.progress > 1) continue;
          updated.push(p);

          const px = p.fromX + (p.toX - p.fromX) * p.progress;
          const py = p.fromY + (p.toY - p.fromY) * p.progress;
          const alpha = p.progress < 0.5 ? p.progress * 2 : (1 - p.progress) * 2;

          ctx.beginPath();
          ctx.arc(px * w, py * h, 3, 0, Math.PI * 2);
          ctx.fillStyle = p.color + Math.floor(alpha * 255).toString(16).padStart(2, '0');
          ctx.fill();

          // Glow
          ctx.beginPath();
          ctx.arc(px * w, py * h, 8, 0, Math.PI * 2);
          ctx.fillStyle = p.color + Math.floor(alpha * 60).toString(16).padStart(2, '0');
          ctx.fill();
        }
        return updated;
      });

      // Draw nodes
      nodes.forEach(n => {
        const x = n.x * w;
        const y = n.y * h;

        // Glow
        if (n.type === 'protocol') {
          ctx.beginPath();
          ctx.arc(x, y, n.size + 8, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(249, 115, 22, 0.08)';
          ctx.fill();
        }

        // Node
        ctx.beginPath();
        ctx.arc(x, y, n.size, 0, Math.PI * 2);
        ctx.fillStyle = COLORS[n.type];
        ctx.fill();

        // Label
        if (n.type !== 'agent') {
          ctx.fillStyle = '#e2e8f0';
          ctx.font = n.type === 'protocol' ? 'bold 10px sans-serif' : '9px sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText(n.label, x, y + n.size + 14);
        }
      });

      animRef.current = requestAnimationFrame(draw);
    };

    draw();
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [nodes]);

  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-3 sm:p-4 overflow-hidden">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs text-slate-500 uppercase tracking-wider">Network Activity</h3>
        <div className="flex items-center gap-3 text-[10px]">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{background: COLORS.provider}}></span> Providers</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{background: COLORS.agent}}></span> Agents</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{background: COLORS.protocol}}></span> Protocol</span>
        </div>
      </div>
      <canvas ref={canvasRef} className="w-full h-64 rounded-lg" />
    </div>
  );
}
